import { ExternalLink } from 'lucide-react'
import type { SalonRow } from '@manaro/contracts'
import { formatDate, formatRelative } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { CardFact, CardFacts, CardHeading, CardStat, CardStats, MobileCard } from '@/shared/ui/responsive-table.tsx'
import { STAGE_LABELS, STAGE_TONES, bookingPageUrl } from '../labels.ts'
import { ownerContactLinks, salonContactLinks } from '../contacts.ts'
import type { RowNotes } from '../notes.ts'
import { ContactButtons } from './contact-links.tsx'
import { NotesCell } from './notes-cell.tsx'
import { Count, ModerationCell, OwnerCell, PlanCell, SalonMeta, SourceCell, Sub } from './salon-cells.tsx'

type Salon = NonNullable<SalonRow['salon']>

function SalonTitle({ salon }: { salon: Salon }) {
  return (
    <>
      {/* The negative margin keeps the line tight while the link stays a 44px tap target. */}
      <a
        href={bookingPageUrl(salon)}
        target="_blank"
        rel="noreferrer"
        className="-my-2.5 flex items-start gap-1.5 py-2.5 text-[15px] leading-snug font-semibold hover:text-brand active:text-brand"
      >
        <span className="line-clamp-2 [overflow-wrap:anywhere]">{salon.title}</span>
        <ExternalLink className="mt-1 size-3.5 shrink-0 text-ink-faint" aria-hidden />
      </a>
      <SalonMeta salon={salon} />
    </>
  )
}

function SalonCounts({ salon }: { salon: Salon }) {
  const recent = [
    salon.orders > 0 && `за 30 дн. ${salon.orders30d}`,
    salon.orders > 0 && salon.lastOrderAt && `последняя ${formatRelative(salon.lastOrderAt)}`,
  ].filter(Boolean)

  return (
    <div>
      <CardStats>
        <CardStat label="Услуг">
          <Count value={salon.services} />
        </CardStat>
        <CardStat label="Клиентов">
          <Count value={salon.customers} />
        </CardStat>
        <CardStat label="Записи">
          <Count value={salon.orders} />
        </CardStat>
      </CardStats>
      {recent.length > 0 && <Sub>{recent.join(' · ')}</Sub>}
    </div>
  )
}

function Owner({ user, isTitle }: { user: SalonRow['user']; isTitle: boolean }) {
  return (
    <div>
      <OwnerCell user={user} className={isTitle ? 'max-w-none text-[15px]' : 'max-w-none'} />
      {user.registeredAt && <Sub>Регистрация {formatDate(user.registeredAt)}</Sub>}
    </div>
  )
}

/** One salon row as a phone card: who and how far first, then counts, source and plan. */
interface SalonCardProps {
  row: SalonRow
  onModerate?: (row: SalonRow) => void
  onGrant?: (salonId: number) => void
  notes?: RowNotes
}

export function SalonCard({ row, onModerate, onGrant, notes }: SalonCardProps) {
  const { user, salon } = row
  const stage = <Badge tone={STAGE_TONES[row.stage]}>{STAGE_LABELS[row.stage]}</Badge>

  return (
    <MobileCard>
      <CardHeading aside={stage}>{salon ? <SalonTitle salon={salon} /> : <Owner user={user} isTitle />}</CardHeading>

      {salon && <Owner user={user} isTitle={false} />}
      {salon && <SalonCounts salon={salon} />}

      <CardFacts>
        {notes && (
          <CardFact label="Комментарии" wide>
            <NotesCell notes={notes.forRow(row)} onOpen={() => notes.onOpen(row)} layout="card" />
          </CardFact>
        )}
        {salon && (
          <CardFact label="Связаться с салоном" wide>
            <ContactButtons links={salonContactLinks(salon)} />
          </CardFact>
        )}
        <CardFact label="Написать владельцу" wide>
          <ContactButtons links={ownerContactLinks(user, salon)} />
        </CardFact>
        <CardFact label="Откуда">
          <SourceCell user={user} />
        </CardFact>
        {salon && (
          <CardFact label="Тариф">
            <PlanCell salon={salon} onGrant={onGrant ? () => onGrant(salon.id) : undefined} />
          </CardFact>
        )}
        {onModerate && salon?.moderation && (
          <CardFact label="Каталог" wide>
            <ModerationCell salon={salon} onOpen={() => onModerate(row)} layout="card" />
          </CardFact>
        )}
      </CardFacts>
    </MobileCard>
  )
}
